import { useMaintenanceWindows, useCreateMaintenanceWindow, useUpdateMaintenanceWindow } from "@/hooks/useEventManagement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Settings, 
  Plus, 
  Calendar, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Pause
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, formatDistanceToNow } from "date-fns";

const maintenanceFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  start_time: z.string().min(1, "Start time is required"),
  end_time: z.string().min(1, "End time is required"),
  affected_services: z.array(z.string()).min(1, "At least one service must be selected")
});

type MaintenanceFormData = z.infer<typeof maintenanceFormSchema>;

const statusConfig = {
  scheduled: {
    icon: Calendar,
    color: "text-blue-500",
    variant: "default" as const,
    label: "Scheduled"
  },
  active: {
    icon: Pause,
    color: "text-orange-500",
    variant: "secondary" as const,
    label: "Active"
  },
  completed: {
    icon: CheckCircle2,
    color: "text-green-500",
    variant: "outline" as const,
    label: "Completed"
  },
  cancelled: {
    icon: XCircle,
    color: "text-red-500",
    variant: "destructive" as const,
    label: "Cancelled"
  }
};

const availableServices = [
  'Event Queue Processor',
  'Email Service',
  'Database',
  'File Storage',
  'Authentication Service',
  'Payment Processing',
  'SMS Service',
  'API Gateway'
];

export function MaintenanceManager() {
  const { data: maintenanceWindows, isLoading } = useMaintenanceWindows();
  const createMaintenanceWindow = useCreateMaintenanceWindow();
  const updateMaintenanceWindow = useUpdateMaintenanceWindow();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const form = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceFormSchema),
    defaultValues: {
      title: "",
      description: "",
      start_time: "",
      end_time: "",
      affected_services: []
    }
  });

  const onSubmit = async (data: MaintenanceFormData) => {
    try {
      await createMaintenanceWindow.mutateAsync({
        title: data.title,
        description: data.description,
        start_time: data.start_time,
        end_time: data.end_time,
        affected_services: data.affected_services
      });
      setIsDialogOpen(false);
      form.reset();
    } catch (error) {
      console.error('Failed to create maintenance window:', error);
    }
  };

  const handleStatusUpdate = (id: string, status: 'active' | 'completed' | 'cancelled') => {
    updateMaintenanceWindow.mutate({ id, status });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Maintenance Windows...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <Settings className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const upcomingWindows = maintenanceWindows?.filter(w => 
    w.status === 'scheduled' && new Date(w.start_time) > new Date()
  ) || [];

  const activeWindows = maintenanceWindows?.filter(w => w.status === 'active') || [];
  const recentWindows = maintenanceWindows?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Maintenance</CardTitle>
            <Pause className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">
              {activeWindows.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently in maintenance mode
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
            <Calendar className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">
              {upcomingWindows.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Scheduled maintenance windows
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actions</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Maintenance
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Schedule Maintenance Window</DialogTitle>
                  <DialogDescription>
                    Plan a maintenance window to perform system updates or repairs
                  </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Database Maintenance" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe what maintenance will be performed..."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="start_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <Input 
                                type="datetime-local"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="end_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>End Time</FormLabel>
                            <FormControl>
                              <Input 
                                type="datetime-local"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="affected_services"
                      render={() => (
                        <FormItem>
                          <FormLabel>Affected Services</FormLabel>
                          <div className="grid grid-cols-2 gap-2">
                            {availableServices.map((service) => (
                              <FormField
                                key={service}
                                control={form.control}
                                name="affected_services"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(service)}
                                        onCheckedChange={(checked) => {
                                          const updatedServices = checked
                                            ? [...(field.value || []), service]
                                            : field.value?.filter(s => s !== service) || [];
                                          field.onChange(updatedServices);
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      {service}
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end space-x-2 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={createMaintenanceWindow.isPending}
                      >
                        {createMaintenanceWindow.isPending ? "Creating..." : "Schedule"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Active Maintenance Alert */}
      {activeWindows.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="h-5 w-5" />
              Active Maintenance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeWindows.map((window) => (
              <div key={window.id} className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-orange-800">{window.title}</h4>
                  <p className="text-sm text-orange-600">{window.description}</p>
                  <p className="text-xs text-orange-500">
                    Started {formatDistanceToNow(new Date(window.start_time), { addSuffix: true })}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleStatusUpdate(window.id, 'completed')}
                  disabled={updateMaintenanceWindow.isPending}
                >
                  Mark Complete
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Maintenance Windows List */}
      <Card>
        <CardHeader>
          <CardTitle>Maintenance Windows</CardTitle>
          <CardDescription>
            All scheduled, active, and completed maintenance windows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentWindows.map((window) => {
              const config = statusConfig[window.status];
              const Icon = config.icon;
              
              return (
                <div key={window.id} className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-full bg-muted">
                      <Icon className={`h-4 w-4 ${config.color}`} />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-medium">{window.title}</h4>
                      <p className="text-sm text-muted-foreground">{window.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {format(new Date(window.start_time), 'MMM dd, yyyy HH:mm')}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          Duration: {Math.round((new Date(window.end_time).getTime() - new Date(window.start_time).getTime()) / 60000)}min
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {window.affected_services.map((service) => (
                          <Badge key={service} variant="outline" className="text-xs">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Badge variant={config.variant}>
                      {config.label}
                    </Badge>
                    
                    {window.status === 'scheduled' && (
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(window.id, 'active')}
                          disabled={updateMaintenanceWindow.isPending}
                        >
                          Start
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(window.id, 'cancelled')}
                          disabled={updateMaintenanceWindow.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    
                    {window.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusUpdate(window.id, 'completed')}
                        disabled={updateMaintenanceWindow.isPending}
                      >
                        Complete
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}